(() => {
  'use strict';
  const normalise = value => String(value ?? '').trim().toLowerCase();
  const optionIndex = (product, kind) => (product.options || []).findIndex(option => {
    const name = normalise(typeof option === 'string' ? option : option.name);
    return kind === 'size' ? name.includes('size') : name.includes('color') || name.includes('colour');
  });
  function matchVariant(product, size, colour) {
    const sizeIndex = optionIndex(product, 'size');
    const colourIndex = optionIndex(product, 'colour');
    if (sizeIndex < 0 || !size) return {variant: null, colour: '', colourFallback: false, available: false};
    const sameSize = (product.variants || []).filter(variant => normalise(variant.options[sizeIndex]) === normalise(size));
    const sameColour = variant => colourIndex < 0 || normalise(variant.options[colourIndex]) === normalise(colour);
    const variant = sameSize.find(item => item.available && sameColour(item))
      || sameSize.find(item => item.available)
      || sameSize.find(sameColour)
      || sameSize[0]
      || null;
    const chosenColour = variant && colourIndex >= 0 ? variant.options[colourIndex] : '';
    return {variant, colour: chosenColour, colourFallback: Boolean(variant && colourIndex >= 0 && normalise(chosenColour) !== normalise(colour)), available: Boolean(variant?.available)};
  }
  function productLink(url, variant, view, base) {
    const target = new URL(url, base);
    if (view) target.searchParams.set('view', view);
    if (variant) target.searchParams.set('variant', variant.id);
    else target.searchParams.delete('variant');
    target.hash = 'ss-sheet-product-options';
    return target.pathname + target.search + target.hash;
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = {matchVariant, productLink, optionIndex};
  if (typeof document === 'undefined') return;

  function init(root) {
    if (root.dataset.ready) return;
    const sourceRoot = root.closest('.ss-approved-pdp');
    let source;
    try { source = JSON.parse(root.querySelector('[data-sheet-source]').textContent); } catch (_) { return; }
    const cards = [...root.querySelectorAll('[data-sheet-card]')].map(card => {
      try { return {card, product: JSON.parse(card.querySelector('[data-sheet-product]').textContent)}; } catch (_) { return null; }
    }).filter(Boolean);
    if (!cards.length) return;
    root.dataset.ready = 'true';
    let previousSelection = '';
    function updateSelection(announce = true) {
      const id = sourceRoot?.dataset.selectedVariantId || root.dataset.selectedVariant;
      const variant = source.variants.find(item => String(item.id) === String(id));
      if (!variant) return;
      const size = variant.options[optionIndex(source, 'size')] || '';
      const colour = variant.options[optionIndex(source, 'colour')] || '';
      const key = `${size}|${colour}`;
      if (key === previousSelection) return;
      previousSelection = key;
      cards.forEach(({card, product}) => {
        const current = card.dataset.current === 'true';
        const result = current
          ? {variant, colour, colourFallback:false, available:Boolean(variant.available)}
          : matchVariant(product, size, colour);
        const chosen = result.variant;
        card.querySelector('[data-sheet-price]').textContent = result.available ? product.prices[String(chosen.id)] : `Unavailable in ${size}`;
        card.querySelector('[data-sheet-selection]').textContent = [size, result.colour].filter(Boolean).join(' · ');
        card.querySelector('[data-sheet-availability]').textContent = result.available
          ? result.colourFallback ? `Alternative colour; ${colour} is not available in this size.` : 'Same size and colour'
          : chosen ? 'Sold out in this size.' : 'This size is not offered.';
        card.querySelector('[data-sheet-link]').href = current ? '#ss-sheet-product-options' : productLink(product.url, chosen, root.dataset.reviewView, window.location.origin);
        card.querySelector('[data-sheet-link-label]').textContent = current ? `Choose your ${card.dataset.family}` : result.available ? `Explore ${card.dataset.family}` : `View ${card.dataset.family} options`;
      });
      root.querySelector('[data-sheet-size-note]').textContent = `Compare the same product type in ${size}. Colours and availability are shown on each card.`;
      if (announce) root.querySelector('[data-sheet-announcement]').textContent = `Comparison updated for ${size}, ${colour}.`;
    }
    updateSelection(false);
    // The approved selector updates this root attribute before replacing the URL.
    // Observe only that public attribute; never alter the existing purchase flow.
    if (sourceRoot) {
      new MutationObserver(() => updateSelection()).observe(sourceRoot, {attributes: true, attributeFilter: ['data-selected-variant-id']});
      sourceRoot.addEventListener('click', event => {
        if (event.target.closest('.ss-option-value')) queueMicrotask(() => updateSelection());
      });
    }
    root.querySelectorAll('a[href="#ss-sheet-product-options"]').forEach(link => link.addEventListener('click', () => {
      requestAnimationFrame(() => document.getElementById('ss-sheet-product-options')?.focus({preventScroll:true}));
    }));

    const videos = [...root.querySelectorAll('[data-sheet-video]')];
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const visible = new Set();
    const manuallyPaused = new Set();
    const load = video => { if (!video.getAttribute('src')) { video.src = video.dataset.videoUrl; video.load(); } };
    const play = video => { load(video); return video.play().catch(() => {}); };
    videos.forEach(video => {
      const button = video.closest('[data-sheet-toggle]');
      const family = button.closest('[data-sheet-card]').dataset.family;
      const sync = () => {
        const action = video.paused ? 'Play' : 'Pause';
        button.setAttribute('aria-label', `${action} ${family} video`);
        button.setAttribute('aria-pressed', String(!video.paused));
        button.querySelector('[data-sheet-video-state]').textContent = action;
      };
      video.muted = true;
      button.addEventListener('click', () => {
        if (button.disabled) return;
        if (video.paused) { manuallyPaused.delete(video); play(video); }
        else { manuallyPaused.add(video); video.pause(); }
      });
      video.addEventListener('play', sync);
      video.addEventListener('pause', sync);
      video.addEventListener('error', () => {
        button.disabled = true;
        button.setAttribute('aria-label', `${family} video unavailable`);
        button.querySelector('[data-sheet-video-state]').textContent = 'Video unavailable';
      });
      sync();
    });
    const resumeVisible = () => {
      if (document.hidden || reducedMotion.matches) return;
      visible.forEach(video => { if (!manuallyPaused.has(video)) play(video); });
    };
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) videos.forEach(video => video.pause());
      else resumeVisible();
    });
    reducedMotion.addEventListener('change', () => { videos.forEach(video => video.pause()); resumeVisible(); });
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(entries => entries.forEach(entry => {
        if (entry.isIntersecting && entry.intersectionRatio >= 0.15) {
          visible.add(entry.target);
          // Reduced-motion users keep the poster until explicitly pressing Play.
          if (!document.hidden && !reducedMotion.matches && !manuallyPaused.has(entry.target)) play(entry.target);
        } else { visible.delete(entry.target); entry.target.pause(); }
      }), {threshold:[0,0.15]});
      videos.forEach(video => observer.observe(video));
    } else {
      // No visibility observer: keep posters and explicit playback, not three eager streams.
      videos.forEach(video => manuallyPaused.add(video));
    }

    const rail = root.querySelector('.ss-sheet-compare__rail');
    const previous = root.querySelector('[data-sheet-previous]');
    const next = root.querySelector('[data-sheet-next]');
    const position = root.querySelector('[data-sheet-position]');
    const index = () => cards.reduce((best, item, i) => Math.abs(item.card.getBoundingClientRect().left - rail.getBoundingClientRect().left) < Math.abs(cards[best].card.getBoundingClientRect().left - rail.getBoundingClientRect().left) ? i : best, 0);
    const updateRail = () => { const i = index(); position.textContent = `${i + 1} of ${cards.length}`; previous.disabled = i === 0; next.disabled = i === cards.length - 1; };
    const move = delta => {
      const target = cards[Math.max(0, Math.min(cards.length - 1, index() + delta))].card;
      rail.scrollBy({left: target.getBoundingClientRect().left - rail.getBoundingClientRect().left, behavior: reducedMotion.matches ? 'auto' : 'smooth'});
    };
    previous.addEventListener('click', () => move(-1));
    next.addEventListener('click', () => move(1));
    rail.addEventListener('keydown', event => {
      if (event.target !== rail || !['ArrowLeft','ArrowRight'].includes(event.key)) return;
      event.preventDefault(); move(event.key === 'ArrowRight' ? 1 : -1);
    });
    rail.addEventListener('scroll', updateRail, {passive:true});
    window.addEventListener('resize', updateRail);
    updateRail();
  }
  const initAll = () => document.querySelectorAll('[data-sheet-compare]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll, {once:true});
  else initAll();
  document.addEventListener('shopify:section:load', initAll);
})();
